export function getLast12Months(
    values: { period: string; value: number | null }[] | undefined,
): number | null {
    if (!values || values.length === 0) {
        return null;
    }

    const sortedValues = values
        .slice()
        .sort((a, b) => a.period.localeCompare(b.period));

    const last12Months = sortedValues.slice(-12);

    const hasValidValue = last12Months.some((item) => item.value !== null);
    if (!hasValidValue) {
        return null;
    }

    return last12Months.reduce((sum, item) => sum + (item.value ?? 0), 0);
}

export function getLastValue<T extends { period: string; value: any | null }>(
    values: T[] | undefined,
): T | null | undefined {
    if (!values || values.length === 0) {
        return null;
    }

    const validValues = values.filter(
        (item) =>
            item.value !== null &&
            item.value !== "N/A" &&
            item.value !== "nan" &&
            item.value !== "NaT",
    );

    if (validValues.length === 0) {
        return null;
    }

    return validValues[validValues.length - 1];
}

/**
 * Gets the value for the closest period on or before the target date.
 * Periods are expected to be in "YYYY-MM" format (e.g., "2022-02", "2025-12")
 *
 * @param values - Array of objects with period and value properties
 * @param targetDate - The date to find data for (Date object or ISO string, defaults to current date)
 * @returns The closest value on or before the target date, or null if none found
 *
 * @example
 * // If today is Jan 9, 2026 and you want the closest month before
 * const data = getValueForDate(metrics, new Date('2026-01-09'));
 * // Returns data from "2025-12" (December 2025)
 */
export function getValueForDate<
    T extends { period: string; value: any | null },
>(values: T[] | undefined, targetDate: Date | string = new Date()): T | null {
    if (!values || values.length === 0) {
        return null;
    }

    if (targetDate instanceof Date && isNaN(targetDate.getTime())) {
        return null;
    }

    const validValues = values.filter(
        (item) =>
            item.value !== null &&
            item.value !== "N/A" &&
            item.value !== "nan" &&
            item.value !== "NaT",
    );

    if (validValues.length === 0) {
        return null;
    }

    const targetYearMonth: string =
        targetDate instanceof Date
            ? (() => {
                  const year = targetDate.getFullYear();
                  const month = String(targetDate.getMonth() + 1).padStart(
                      2,
                      "0",
                  );
                  return `${year}-${month}`;
              })()
            : targetDate.substring(0, 7);

    const sortedValues = validValues
        .slice()
        .sort((a, b) => a.period.localeCompare(b.period));

    const closestValue = sortedValues
        .filter((item) => item.period <= targetYearMonth)
        .pop();

    return closestValue ?? null;
}
